import React, { useRef, useEffect, useCallback } from 'react';
import type { Adventure } from '../types';

interface Props {
  adventures: Adventure[];
  title?: string;
  onSelectAdventure?: (adventure: Adventure) => void;
}

export function WorldMap({ adventures, title, onSelectAdventure }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const mapIdRef = useRef(`map-${Math.random().toString(36).slice(2, 10)}`);

  // Build chronological list of points
  const chronological = [...adventures].reverse();

  const allPoints = chronological.flatMap(adv =>
    adv.entries
      .filter(e => e.lat != null && e.lng != null)
      .map(e => ({
        lat: e.lat!,
        lng: e.lng!,
        header: e.header,
        city: e.city || '',
        adventureTitle: adv.description,
        adventureId: adv.id,
      }))
  );

  const initMap = useCallback(() => {
    if (!mapRef.current || allPoints.length === 0) return;
    const L = (window as any).L;
    if (!L) return;

    // Cleanup existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
      worldCopyJump: true,
    });

    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 18,
    }).addTo(map);

    const gradientColor = (t: number): string => {
      if (t < 0.33) {
        const p = t / 0.33;
        return `rgb(${Math.round(34 + p * 200)},${Math.round(197 + p * (179 - 197))},${Math.round(94 + p * (8 - 94))})`;
      } else if (t < 0.66) {
        const p = (t - 0.33) / 0.33;
        return `rgb(234,${Math.round(179 + p * (88 - 179))},${Math.round(8 + p * 4)})`;
      } else {
        const p = (t - 0.66) / 0.34;
        return `rgb(${Math.round(234 - p * 14)},${Math.round(88 - p * 50)},${Math.round(12 + p * 26)})`;
      }
    };

    const coords: [number, number][] = [];
    const adjusted: [number, number][] = [];
    const n = allPoints.length;

    allPoints.forEach((pt, i) => {
      let lat = pt.lat;
      let lng = pt.lng;

      // 🔥 Dateline-safe adjustment (single source of truth)
      if (adjusted.length > 0) {
        const prev = adjusted[adjusted.length - 1];
        while (lng - prev[1] > 180) lng -= 360;
        while (lng - prev[1] < -180) lng += 360;
      }

      const adj: [number, number] = [lat, lng];

      coords.push(adj);
      adjusted.push(adj);

      const t = n > 1 ? i / (n - 1) : 0;
      const color = gradientColor(t);

      const marker = L.circleMarker(adj, {
        radius: 5,
        fillColor: color,
        color: '#fff',
        weight: 1.5,
        fillOpacity: 0.85,
      }).addTo(map);

      const label = pt.city || pt.header;

      marker.bindPopup(
        `<div style="font-family:sans-serif;font-size:13px;">
          <strong>${pt.adventureTitle}</strong>
          <br/>${pt.header}
          ${label !== pt.header ? `<br/><span style="color:#666">${label}</span>` : ''}
        </div>`,
        { maxWidth: 220 }
      );

      if (onSelectAdventure) {
        marker.on('click', () => {
          const adv = adventures.find(a => a.id === pt.adventureId);
          if (adv) onSelectAdventure(adv);
        });
      }
    });

    // Draw route
    if (coords.length > 1) {
      for (let i = 0; i < coords.length - 1; i++) {
        const t = i / (coords.length - 1);
        L.polyline([coords[i], coords[i + 1]], {
          color: gradientColor(t),
          weight: 2.5,
          opacity: 0.6,
        }).addTo(map);
      }
    }

    // Fit bounds
    if (coords.length === 1) {
      map.setView(coords[0], 6);
    } else {
      map.fitBounds(L.latLngBounds(coords), { padding: [20, 20] });
    }

  }, [allPoints, adventures, onSelectAdventure]);

  useEffect(() => {
    const L = (window as any).L;

    if (L) {
      initMap();
      return;
    }

    // Load Leaflet dynamically
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => initMap();
    document.head.appendChild(script);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [initMap]);

  useEffect(() => {
    if ((window as any).L) initMap();
  }, [initMap]);

  if (allPoints.length === 0) return null;

  const adventuresWithLocations = adventures.filter(a =>
    a.entries.some(e => e.lat != null && e.lng != null)
  ).length;

  const displayTitle = title
    ? `${title} — ${allPoints.length} locations across ${adventuresWithLocations} adventure${adventuresWithLocations !== 1 ? 's' : ''}`
    : `All Journeys — ${allPoints.length} locations across ${adventuresWithLocations} adventure${adventuresWithLocations !== 1 ? 's' : ''}`;

  return (
    <div className="card overflow-hidden mb-4" style={{ position: 'relative', zIndex: 0 }}>
      <div className="px-4 py-2 border-b border-sand-100 flex items-center justify-between">
        <span className="text-xs font-medium text-sand-500 uppercase tracking-wide">
          {displayTitle}
        </span>
      </div>
      <div ref={mapRef} id={mapIdRef.current} style={{ height: 280, width: '100%' }} />
    </div>
  );
}
